/**
 * Google Tag Manager + GA4 loader for the React SPA (app.humanriskacademy.com).
 *
 * This domain is the SECOND half of the WZ cross-domain setup: the marketing
 * site (w-z.com) hands visitors off here to register, so both domains must
 * carry the SAME GTM container for GA4 to stitch that journey into one session.
 *
 * Two loaders, never both (see the `analytics-events` skill):
 *   - VITE_GTM_ID       loads the container. Tags live in the GTM UI.
 *   - VITE_GA4_DIRECT   loads gtag.js directly, because the container is not yet
 *                       published. Flip to "false" the day it is.
 *
 * Consent: `pushConsentDefaults()` runs BEFORE either loader, so nothing sets a
 * cookie until the visitor accepts via ConsentBanner. Denied traffic still
 * reaches GA4 as cookieless pings.
 */

import { pushConsentDefaults, replayStoredConsent } from './consent';
import { gtag } from './gtag';

const GTM_ID = import.meta.env.VITE_GTM_ID as string | undefined;
const GA4_DIRECT = (import.meta.env.VITE_GA4_DIRECT as string | undefined) === 'true';

const GTM_SCRIPT_ID = 'gtm-loader';
const GA4_SCRIPT_ID = 'ga4-loader';

/** Not an env var: the measurement ID is public and does not vary per env. */
export const GA4_MEASUREMENT_ID = 'G-CKZXSD14E6';

export const isGa4Direct = (): boolean => GA4_DIRECT;

let initialized = false;

function injectScript(id: string, src: string): void {
  if (document.getElementById(id)) return;
  const script = document.createElement('script');
  script.id = id;
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

/**
 * Declare consent, replay any stored decision, then load whichever tag source is
 * configured. Idempotent. Safe to call with neither loader configured (local
 * dev): consent defaults are just an array append.
 */
export function initGtm(): void {
  if (initialized) return;
  initialized = true;

  window.dataLayer = window.dataLayer || [];

  // Order matters: defaults, then the stored decision, then the loaders. Both
  // land inside the `wait_for_update` window so tags never evaluate against a
  // stale `denied`.
  pushConsentDefaults();
  replayStoredConsent();

  if (GTM_ID) {
    window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    injectScript(GTM_SCRIPT_ID, `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`);
  }

  if (GA4_DIRECT) {
    injectScript(GA4_SCRIPT_ID, `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`);
    // Must go through gtag() — these are command tuples, not object pushes.
    gtag('js', new Date());
    gtag('config', GA4_MEASUREMENT_ID);
  }
}

/**
 * Report a client-side navigation. A SPA loads index.html once, so GTM never
 * sees React Router route changes as pageviews unless we push them. The GTM
 * container needs a matching "Custom Event: spa_pageview" trigger.
 */
export function trackPageview(path: string): void {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: 'spa_pageview', page_path: path });
}
